import { useEffect, useRef } from 'react';

import { recordRequestRead } from '../services/requestReadMarker';

/**
 * Record that the person has opened this request — once per mount (#1575 story 3.2).
 *
 * ⚠️ FIRE AND FORGET, BY CONSTRUCTION. The effect `void`s the promise and
 * `recordRequestRead` never rejects, so there is nothing here for the screen to
 * await, show a spinner for or report. A read marker that does not arrive costs
 * one bold row, and the next open tries again.
 *
 * ⚠️ NEVER FOR AN `o-` DRAFT. An offline-create id is a local placeholder: there
 * is no server request to mark read, and queueing one would leave an entry
 * pointing at an id that stops existing the moment the create syncs. The guard
 * lives here rather than in the service so the service has one job, and so this
 * is the single place a caller can get it wrong.
 *
 * ⚠️ IT DOES NOT WAIT FOR THE DETAIL FETCH. `POST /requests/:id/read` is behind
 * the same `requireRequestAccess('view')` as `GET /requests/:id`, so a request the
 * viewer may not open cannot have its marker moved either — sequencing the two
 * would buy nothing and would delay the marker by a round trip. `readAt` is
 * stamped here, at the open, for the same reason: it is when the person looked,
 * not when the network got round to saying so.
 *
 * The "once" is keyed on the id, not on the mount: `RequestDetail` re-renders
 * constantly (query polls, edit state), and web navigation between two requests
 * can reuse the same mount. A new id fires; the same id never fires twice.
 */
export const useRecordRequestRead = (
  requestId: string | null | undefined,
): void => {
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!requestId || requestId.startsWith('o-')) {
      return;
    }
    if (firedForRef.current === requestId) {
      return;
    }
    firedForRef.current = requestId;
    // `recordRequestRead` is documented never to reject, and this does not rely
    // on that: an unhandled rejection here would be a red box on iOS and a
    // failed test run, for a call whose whole contract is that nobody is
    // listening. The service has already logged whatever went wrong.
    recordRequestRead(requestId, new Date().toISOString()).catch(() => {
      /* fire and forget */
    });
  }, [requestId]);
};
